'use client';
import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import FlashComponent from '../basic/FlashComponent';
import LoadMore from '../index/LoadMore';
import { getFlashListByPage } from '@/app/actions';

const FlashList = ({ locale, selectedDate }) => {
    const { t } = useTranslation();

    const [isMount, setIsMount] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [pageIndex, setPageIndex] = useState({ total: 0, important: 0 });
    const [pageCnt, setPageCnt] = useState({ total: [], important: 0 });
    const [showImportant, setShowImportant] = useState('total');
    const [flashList, setFlashList] = useState({ total: [], important: [] });
    const [previousDate, setPreviousDate] = useState(selectedDate); // 前一個日期

    // 取得快訊資料
    const getMoreFlashList = async (type) => {
        // 判斷日期
        const selectedDay = dayjs(selectedDate).isAfter(dayjs())
            ? dayjs().format('YYYY-MM-DD')
            : dayjs(selectedDate).format('YYYY-MM-DD');

        try {
            setLoadingMore(true);
            const result = await getFlashListByPage(
                locale,
                type === 'init' ? 0 : pageIndex[showImportant],
                20,
                type === 'init' ? '' : showImportant === 'important' ? '&appPush=1' : '',
                null,
                selectedDay
            );
            if (result) {
                const { records, pageCnt: newPageCnt } = result;
                setPageCnt((prev) => ({ ...prev, [showImportant]: newPageCnt }));
                setFlashList((prev) => ({
                    ...prev,
                    [showImportant]: [...prev[showImportant], ...records],
                }));
                setPageIndex((prev) => ({ ...prev, [showImportant]: prev[showImportant] + 1 }));
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoadingMore(false);
        }
    };

    // 切換重要訊息
    const toggleImportant = () =>
        setShowImportant((prev) => (prev === 'important' ? 'total' : 'important'));

    // 頁面切換重置
    const resetFlashList = useCallback(async () => {
        const today = dayjs().format('YYYY-MM-DD');
        const isTodayOrFutureSelected = dayjs(selectedDate).isSameOrAfter(today);
        const isPreviousDateFuture = dayjs(previousDate).isSameOrAfter(today);

        // 當選擇的日期為今天或未來且之前的日期在未來時，直接返回
        if (isTodayOrFutureSelected && isPreviousDateFuture) return;

        setPageIndex({ total: 0, important: 0 });
        setPageCnt({ total: 0, important: 0 });
        setShowImportant('total');
        setFlashList({ total: [], important: [] });

        await getMoreFlashList('init');

        setPreviousDate(selectedDate);
    }, [selectedDate]);

    useEffect(() => {
        getMoreFlashList();
    }, [showImportant]);

    useEffect(() => {
        if (isMount) resetFlashList();
        setIsMount(true);
    }, [selectedDate]);

    return (
        <div className="flex-grow">
            <section className="pr-[70px] uxl:pr-0 umd:space-y-[5px] relative mt-[20px]">
                {flashList?.[showImportant]?.map((flash, i) => {
                    // 判斷日期 差異
                    const currentFlashDate = dayjs(flash?.createdTime)?.format('YYYY.MM.DD');
                    const previousFlashDate =
                        i > 0
                            ? dayjs(flashList?.[showImportant]?.[i - 1]?.createdTime)?.format(
                                  'YYYY.MM.DD'
                              )
                            : null;

                    // 判斷是否為當天的第一個項目
                    const isFirstOfDay = currentFlashDate !== previousFlashDate;

                    return (
                        <React.Fragment key={flash?.id + 'flash-list' + i}>
                            {isFirstOfDay && (
                                <>
                                    <p className="text-bd2n text-pale9 mb-[20px]">
                                        {dayjs(flash?.createdTime)?.format('YYYY.MM.DD')}
                                    </p>
                                    {/* 重要快訊篩選 */}
                                    <button
                                        onClick={toggleImportant}
                                        className={`text-btn1 center-center gap-x-[10px] absolute top-[7px] right-0 z-[1] ${
                                            showImportant === 'important'
                                                ? 'text-pale '
                                                : 'text-black9'
                                        }`}
                                    >
                                        <span
                                            className={`w-[18px] h-[18px] rounded-[4px] text-bd4n border-1 text-center text-white 
                                            ${
                                                showImportant === 'important'
                                                    ? 'border-pale bg-pale'
                                                    : 'border-black2 '
                                            }`}
                                        >
                                            ✓
                                        </span>
                                        {t('IMPORTANT')}
                                    </button>
                                </>
                            )}
                            <FlashComponent
                                flash={flash}
                                isLast={i + 1 === flashList?.[showImportant]?.length}
                            />
                        </React.Fragment>
                    );
                })}
            </section>

            {/* 更多 */}
            {pageCnt?.[showImportant] > pageIndex?.[showImportant] &&
                flashList?.[showImportant]?.length > 0 && (
                    <LoadMore loadingMore={loadingMore} onLoad={getMoreFlashList} />
                )}
        </div>
    );
};

export default FlashList;
